import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useAuth, useMe } from '../auth';
import { useOnline } from './ui';
import { isUnsynced, useDraftSync, useLocalDrafts } from '../offline';
import { novidadesPendentes } from '../pages/Novidades';

// Menu principal da seção 4, agrupado pelo uso: o dia a dia primeiro, o controle depois, a administração por último.
const MENU: { group: string; items: { to: string; label: string; stage?: string }[] }[] = [
  {
    group: 'Dia a dia',
    items: [
      { to: '/novidades', label: 'Novidades e como fazer' },
      { to: '/', label: 'Meu ambiente' },
      { to: '/agenda', label: 'Agenda' },
      { to: '/execucoes', label: 'Execuções (atendimentos)' },
      { to: '/alertas', label: 'Alertas e lembretes' },
    ],
  },
  {
    group: 'Controle',
    items: [
      { to: '/projetos', label: 'Projetos' },
      { to: '/bancos-de-horas', label: 'Bancos de horas' },
      { to: '/faturamento', label: 'Faturamento' },
      { to: '/relatorios', label: 'Relatórios' },
      { to: '/visao-geral', label: 'Visão geral' },
    ],
  },
  {
    group: 'Administração',
    items: [
      { to: '/cadastros', label: 'Cadastros' },
      { to: '/configuracoes', label: 'Configurações' },
    ],
  },
];

export function Layout() {
  const me = useMe();
  const { logout } = useAuth();
  const online = useOnline();
  const [menuOpen, setMenuOpen] = useState(false);
  const [novo, setNovo] = useState(novidadesPendentes);
  useEffect(() => {
    const f = () => setNovo(novidadesPendentes());
    window.addEventListener('novidades-vistas', f);
    return () => window.removeEventListener('novidades-vistas', f);
  }, []);
  const location = useLocation();
  useDraftSync(me.id);
  const unsynced = useLocalDrafts(me.id).filter(isUnsynced).length;
  // Sino: alertas internos não lidos + lembretes sugeridos (atualiza a cada minuto).
  const bell = useQuery({
    queryKey: ['alerts-summary'],
    queryFn: () => api.get<{ open: number; unread: number; reminders: number }>('/alerts/summary'),
    refetchInterval: 60_000,
    enabled: online,
  });
  const bellCount = (bell.data?.unread ?? 0) + (bell.data?.reminders ?? 0);

  // Rascunhos não sincronizados ficam no aparelho, no espaço desta conta; outra conta não os vê.
  const confirmLogout = () => {
    if (unsynced > 0 && !window.confirm(`Há ${unsynced} rascunho(s) ainda não enviados ao servidor. Eles continuarão guardados neste aparelho para a sua conta. Sair mesmo assim?`)) return;
    void logout();
  };

  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <div className="app">
      <a className="skip-link" href="#conteudo">
        Pular para o conteúdo
      </a>
      <aside className={`sidebar${menuOpen ? ' open' : ''}`} aria-label="Menu principal">
        <div className="brand">
          TECHNOVE
          <small>Projetos e consultorias</small>
        </div>
        <nav className="nav">
          {MENU.map((g) => (
            <div key={g.group} role="group" aria-label={g.group} style={{ display: 'contents' }}>
              <div className="nav-group">{g.group}</div>
              {g.items.map((m) => (
                <NavLink key={m.to} to={m.to} end={m.to === '/'}>
                  <span>{m.label}</span>
                  {m.stage && <span className="stage" title={`Disponível na etapa ${m.stage}`}>{m.stage}</span>}
                  {m.to === '/novidades' && novo && <span className="badge-novo">novo</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div>
            Conectado como <strong>{me.consultantName}</strong>
          </div>
          <button className="btn btn-sm" onClick={confirmLogout}>
            Sair
          </button>
        </div>
      </aside>
      {menuOpen && <div className="dialog-backdrop show-mobile" style={{ zIndex: 35 }} onClick={() => setMenuOpen(false)} />}

      <div className="main">
        <header className="topbar">
          <button className="btn btn-ghost btn-sm show-mobile" onClick={() => setMenuOpen(true)} aria-label="Abrir menu">
            ☰ Menu
          </button>
          <span className="who">
            Consultor atual: <strong>{me.consultantName}</strong>
          </span>
          <span className="actions" style={{ alignItems: 'center' }}>
            {unsynced > 0 && (
              <Link to="/execucoes" className="badge badge-demo" title="Rascunhos ainda não enviados ao servidor">
                {unsynced} rascunho(s) só no aparelho
              </Link>
            )}
            <Link
              to="/alertas"
              className={`bell${bellCount ? ' has' : ''}`}
              aria-label={bellCount ? `${bellCount} alerta(s) ou lembrete(s) pendente(s)` : 'Alertas e lembretes'}
              title={bell.data ? `${bell.data.unread} alerta(s) não lido(s) · ${bell.data.reminders} lembrete(s) sugerido(s)` : 'Alertas e lembretes'}
            >
              <span aria-hidden>🔔</span>
              {bellCount > 0 && <span className="bell-count">{bellCount > 99 ? '99+' : bellCount}</span>}
            </Link>
            <span className={`conn ${online ? 'online' : 'offline'}`} role="status" aria-live="polite">
              {online ? 'Conectado' : 'Sem conexão'}
            </span>
          </span>
        </header>
        <main className="content" id="conteudo" tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      {/* Celular: prioriza Agenda, Nova execução e Meus registros (seção 4) */}
      <nav className="bottom-nav" aria-label="Atalhos">
        <NavLink to="/" end>
          <span className="ico" aria-hidden>⌂</span>Início
        </NavLink>
        <NavLink to="/agenda">
          <span className="ico" aria-hidden>▦</span>Agenda
        </NavLink>
        <NavLink to="/execucoes/nova">
          <span className="ico" aria-hidden>＋</span>Nova execução
        </NavLink>
        <NavLink to="/execucoes" end>
          <span className="ico" aria-hidden>☰</span>Meus registros
        </NavLink>
        <button onClick={() => setMenuOpen(true)}>
          <span className="ico" aria-hidden>⋯</span>Mais
        </button>
      </nav>
    </div>
  );
}
